// Geolocation Service for City Detection
// Uses multiple methods for accurate city detection in Morocco

// Price Regions:
// CASA_REGION: Uses price_casa (Casablanca and nearby cities)
// FES_REGION: Uses regular price (Fes and nearby cities)
const PRICE_REGIONS = {
    CASA: 'casa',  // Uses price_casa
    FES: 'fes',    // Uses regular price
};

const MOROCCAN_CITIES = [
    // Casa Region - uses price_casa
    { id: 1, name: 'Casablanca', lat: 33.5731, lng: -7.5898, region: PRICE_REGIONS.CASA },
    { id: 2, name: 'Rabat', lat: 34.0209, lng: -6.8416, region: PRICE_REGIONS.CASA },
    { id: 4, name: 'Marrakech', lat: 31.6295, lng: -7.9811, region: PRICE_REGIONS.CASA },
    { id: 5, name: 'Tanger', lat: 35.7595, lng: -5.8340, region: PRICE_REGIONS.CASA },
    { id: 6, name: 'Agadir', lat: 30.4278, lng: -9.5981, region: PRICE_REGIONS.CASA },
    { id: 9, name: 'Tétouan', lat: 35.5889, lng: -5.3626, region: PRICE_REGIONS.CASA },
    { id: 10, name: 'Safi', lat: 32.2994, lng: -9.2372, region: PRICE_REGIONS.CASA },
    { id: 11, name: 'Kénitra', lat: 34.2610, lng: -6.5802, region: PRICE_REGIONS.CASA },
    { id: 12, name: 'Salé', lat: 34.0531, lng: -6.7985, region: PRICE_REGIONS.CASA },
    { id: 13, name: 'Mohammedia', lat: 33.6866, lng: -7.3830, region: PRICE_REGIONS.CASA },
    { id: 14, name: 'El Jadida', lat: 33.2316, lng: -8.5007, region: PRICE_REGIONS.CASA },
    { id: 16, name: 'Beni Mellal', lat: 32.3373, lng: -6.3498, region: PRICE_REGIONS.CASA },
    { id: 18, name: 'Khémisset', lat: 33.8242, lng: -6.0664, region: PRICE_REGIONS.CASA },
    
    // Fes Region - uses regular price
    { id: 3, name: 'Fès', lat: 34.0181, lng: -5.0078, region: PRICE_REGIONS.FES },
    { id: 7, name: 'Meknès', lat: 33.8935, lng: -5.5473, region: PRICE_REGIONS.FES },
    { id: 8, name: 'Oujda', lat: 34.6867, lng: -1.9114, region: PRICE_REGIONS.FES },
    { id: 15, name: 'Nador', lat: 35.1681, lng: -2.9287, region: PRICE_REGIONS.FES },
    { id: 17, name: 'Taza', lat: 34.2300, lng: -4.0100, region: PRICE_REGIONS.FES },
    { id: 19, name: 'Sefrou', lat: 33.8303, lng: -4.8353, region: PRICE_REGIONS.FES },
    { id: 20, name: 'Ifrane', lat: 33.5228, lng: -5.1109, region: PRICE_REGIONS.FES },
    { id: 21, name: 'Moulay Yacoub', lat: 34.0875, lng: -5.1797, region: PRICE_REGIONS.FES },
    { id: 22, name: 'Taounate', lat: 34.5367, lng: -4.6400, region: PRICE_REGIONS.FES },
    { id: 23, name: 'Azrou', lat: 33.4342, lng: -5.2214, region: PRICE_REGIONS.FES },
    { id: 24, name: 'Boulemane', lat: 33.3625, lng: -4.7306, region: PRICE_REGIONS.FES },
    { id: 25, name: 'Guercif', lat: 34.2264, lng: -3.3517, region: PRICE_REGIONS.FES },
];

const STORAGE_KEY = 'user_city';
const STORAGE_EXPIRY_KEY = 'user_city_expiry';
const EXPIRY_HOURS = 24; // Refresh location every 24 hours

class GeolocationService {
    constructor() {
        this.currentCity = null;
        this.cities = MOROCCAN_CITIES;
    }

    // Get all available cities
    getCities() {
        return this.cities;
    }

    // Get stored city from localStorage
    getStoredCity() {
        try {
            const storedCity = localStorage.getItem(STORAGE_KEY);
            const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
            
            if (storedCity && expiry) {
                const now = new Date().getTime();
                if (now < parseInt(expiry)) {
                    return JSON.parse(storedCity);
                }
            }
        } catch (error) {
            console.error('Error reading stored city:', error);
        }
        return null;
    }

    // Store city in localStorage
    storeCity(city) {
        try {
            const expiry = new Date().getTime() + (EXPIRY_HOURS * 60 * 60 * 1000);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(city));
            localStorage.setItem(STORAGE_EXPIRY_KEY, expiry.toString());
            this.currentCity = city;
        } catch (error) {
            console.error('Error storing city:', error);
        }
    }

    // Calculate distance between two points using Haversine formula
    calculateDistance(lat1, lng1, lat2, lng2) {
        const R = 6371; // Earth's radius in km
        const dLat = this.toRad(lat2 - lat1);
        const dLng = this.toRad(lng2 - lng1);
        const a = 
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    toRad(deg) {
        return deg * (Math.PI / 180);
    }

    // Find nearest city based on coordinates
    findNearestCity(lat, lng) {
        let nearestCity = this.cities[0];
        let minDistance = Infinity;

        for (const city of this.cities) {
            const distance = this.calculateDistance(lat, lng, city.lat, city.lng);
            if (distance < minDistance) {
                minDistance = distance;
                nearestCity = city;
            }
        }

        return nearestCity;
    }

    // Method 1: Browser Geolocation API (most accurate)
    async detectByBrowserGeolocation() {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation not supported'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const nearestCity = this.findNearestCity(latitude, longitude);
                    resolve(nearestCity);
                },
                (error) => {
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000 // 5 minutes cache
                }
            );
        });
    }

    // Method 2: IP-based geolocation using free API
    async detectByIP() {
        try {
            // Using ip-api.com (free, no API key needed, 45 requests per minute)
            const response = await fetch('http://ip-api.com/json/?fields=status,city,lat,lon');
            const data = await response.json();
            
            if (data.status === 'success') {
                // Try to match city name first
                const cityByName = this.cities.find(
                    c => c.name.toLowerCase() === data.city?.toLowerCase()
                );
                
                if (cityByName) {
                    return cityByName;
                }
                
                // If no exact match, find nearest city
                if (data.lat && data.lon) {
                    return this.findNearestCity(data.lat, data.lon);
                }
            }
            
            throw new Error('IP geolocation failed');
        } catch (error) {
            // Fallback to ipapi.co
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                
                if (data.city) {
                    const cityByName = this.cities.find(
                        c => c.name.toLowerCase() === data.city?.toLowerCase()
                    );
                    
                    if (cityByName) {
                        return cityByName;
                    }
                    
                    if (data.latitude && data.longitude) {
                        return this.findNearestCity(data.latitude, data.longitude);
                    }
                }
            } catch (fallbackError) {
                console.error('Fallback IP detection failed:', fallbackError);
            }
            
            throw error;
        }
    }

    // Main detection method - tries multiple methods
    async detectCity() {
        // First check localStorage
        const storedCity = this.getStoredCity();
        if (storedCity) {
            this.currentCity = storedCity;
            return storedCity;
        }

        // Try Browser Geolocation first (most accurate)
        try {
            const city = await this.detectByBrowserGeolocation();
            this.storeCity(city);
            return city;
        } catch (browserError) {
            console.log('Browser geolocation failed, trying IP detection...');
        }

        // Fallback to IP-based detection
        try {
            const city = await this.detectByIP();
            this.storeCity(city);
            return city;
        } catch (ipError) {
            console.log('IP detection failed, using default city');
        }

        // Default to Casablanca if all methods fail
        const defaultCity = this.cities[0]; // Casablanca
        this.storeCity(defaultCity);
        return defaultCity;
    }

    // Manually set city (when user selects from dropdown)
    setCity(cityId) {
        const city = this.cities.find(c => c.id === parseInt(cityId));
        if (city) {
            this.storeCity(city);
            return city;
        }
        return null;
    }

    // Get current city
    getCurrentCity() {
        if (this.currentCity) {
            return this.currentCity;
        }
        return this.getStoredCity() || this.cities[0];
    }

    // Clear stored location (for testing or reset)
    clearStoredLocation() {
        try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(STORAGE_EXPIRY_KEY);
            this.currentCity = null;
        } catch (error) {
            console.error('Error clearing stored location:', error);
        }
    }

    // Check if current city is in Casa region (uses price_casa)
    isCasaRegion() {
        const city = this.getCurrentCity();
        return city?.region === PRICE_REGIONS.CASA;
    }

    // Check if current city is in Fes region (uses regular price)
    isFesRegion() {
        const city = this.getCurrentCity();
        return city?.region === PRICE_REGIONS.FES;
    }

    // Get the correct price based on user's location
    // Returns price_casa for Casa region, regular price for Fes region
    getProductPrice(product) {
        if (!product) return 0;
        
        const city = this.getCurrentCity();
        
        // If in Casa region and price_casa exists, use it
        if (city?.region === PRICE_REGIONS.CASA && product.price_casa) {
            return parseFloat(product.price_casa);
        }
        
        // Otherwise use regular price
        return parseFloat(product.price || 0);
    }

    // Get price region name for display
    getPriceRegionName() {
        const city = this.getCurrentCity();
        return city?.region === PRICE_REGIONS.CASA ? 'Casablanca' : 'Fès';
    }
}

// Export singleton instance
const geolocationService = new GeolocationService();
export default geolocationService;
export { MOROCCAN_CITIES, PRICE_REGIONS };
